import type { PartLine, VoicePart } from '@corocanto/scoring';
import { useTranslation } from 'react-i18next';
import type { Channel, ChannelSetting, Mixer as MixerState } from '../audio/mix';
import { ModeIcon } from './ModeIcon';
import styles from './Mixer.module.css';

interface Props {
  channels: readonly Channel[];
  mixer: MixerState;
  myPart: VoicePart | null;
  /** The line the chorist sings where their part divides: their channel and "other" are then named by line. */
  myLine?: PartLine | null;
  /** Channels the song does not have (e.g. no accompaniment): shown, but disabled. */
  disabled?: readonly Channel[];
  onChange: (channel: Channel, setting: ChannelSetting) => void;
}

/**
 * Compact mixer for the controls panel of the practice and rehearsal pages: a vertical volume
 * fader per channel above a button with the part's initial (the keyboard icon for the
 * accompaniment, "Eşlik"). The button mutes and unmutes; full names show as hints. A solo button
 * sits under it: with any solo on, only the soloed channels play, and the others are dimmed.
 */
export function Mixer({ channels, mixer, myPart, myLine = null, disabled = [], onChange }: Props) {
  const { t } = useTranslation();
  const anySolo = channels.some((ch) => mixer[ch].solo);
  // In a divided part, my channel and the other line are "1. Soprano" and "2. Soprano" (S1, S2).
  const lineOf = (ch: Channel): PartLine | null =>
    !myPart || !myLine ? null : ch === myPart ? myLine : ch === 'other' ? (myLine === 1 ? 2 : 1) : null;
  const label = (ch: Channel) => {
    const line = lineOf(ch);
    if (line && myPart) return t('parts.line', { part: t(`parts.${myPart}`), line });
    return t(ch === 'acc' ? 'parts.accompaniment' : `parts.${ch}`);
  };
  const letter = (ch: Channel) => {
    const line = lineOf(ch);
    if (line && myPart) return t('partLetters.line', { part: t(`partLetters.${myPart}`), line });
    return t(`partLetters.${ch}`);
  };
  return (
    <div className={styles.mixer} data-testid="mixer">
      <span className={styles.label} id="mixer-label">
        {t('practice.mixer')}
      </span>
      <div className={styles.channels} role="group" aria-labelledby="mixer-label">
        {channels.map((ch) => {
          const setting = mixer[ch];
          const name = label(ch);
          const off = disabled.includes(ch);
          return (
            <div key={ch} className={`${styles.channel} ${anySolo && !setting.solo ? styles.silenced : ''}`} data-channel={ch}>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={setting.volume}
                aria-label={t('practice.volumeOf', { part: name })}
                title={off ? t('practice.noAccompaniment') : t('practice.volumeOf', { part: name })}
                disabled={off}
                className={`${styles.fader} ${setting.muted ? styles.faderMuted : ''}`}
                onChange={(e) => onChange(ch, { ...setting, volume: Number(e.target.value) })}
              />
              <button
                type="button"
                className={`${styles.mute} ${ch === myPart ? styles.mine : ''} ${setting.muted ? styles.muted : ''}`}
                aria-pressed={setting.muted}
                aria-label={t('practice.muteOf', { part: name })}
                data-tip={
                  off
                    ? t('practice.noAccompaniment')
                    : setting.muted
                      ? t('practice.unmuteTip', { part: name })
                      : t('practice.muteTip', { part: name })
                }
                disabled={off}
                onClick={() => onChange(ch, { ...setting, muted: !setting.muted })}
              >
                {ch === 'acc' ? <ModeIcon mode="accompaniment" /> : letter(ch)}
              </button>
              <button
                type="button"
                className={`${styles.solo} ${setting.solo ? styles.soloOn : ''}`}
                aria-pressed={setting.solo}
                aria-label={t('practice.soloOf', { part: name })}
                data-tip={t('practice.soloOf', { part: name })}
                disabled={off}
                onClick={() => onChange(ch, { ...setting, solo: !setting.solo })}
              >
                {t('practice.solo')}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
